/* Model routing — built to the Claude Design reference's "Model routing" screen (`Lumiq App.dc.html`).
 *
 * Which backend serves this tenant's AI features. `core.ai.serving` had a complete API, a generated
 * client and no importer, so routing was configurable only by curl and `LLM_SERVING` reported
 * MISSING on every deployment — which made five working AI features look broken. This is the screen
 * that closes that: one PUT, and the whole AI plane has something to answer with.
 *
 * **Three states, not two, and the reference makes that its thesis rather than a detail.** Its lede
 * says they "must never collapse into two", and its layout is a three-card legend in which the card
 * matching the tenant's actual state carries the live values. An unrouted tenant, a route somebody
 * switched off, and a live route are different facts: `core.ai.anomaly` reports the middle one as
 * `TEMPLATE_ONLY` *by configuration* where an unreachable endpoint is `UNAVAILABLE`, and collapsing
 * them once already meant an admin closing a maintenance window had every anomalous finding reported
 * as a platform outage.
 *
 * **The reference shows no form, and the form stays.** It is a static mockup of the states; removing
 * the only control that can configure a route would remove the feature. It is styled in the
 * reference's own card language instead.
 *
 * **Routing is per tenant and stored, never an environment variable.** A bank on the shared tier may
 * contractually require that its metadata never reaches a hosted endpoint while its neighbour has no
 * such term; a deployment-wide setting cannot express that.
 *
 * **The screen never asks for a key, and says so** — the reference's closing line, kept verbatim.
 * The credential is deployment configuration (`LUMIQ_LLM_API_KEY`) with an endpoint allow-list,
 * because `endpoint` here is *tenant*-controlled: attaching a shared key to whatever host a tenant
 * named would hand one customer the platform's credential.
 *
 * **`never_sent`, the rung and the validator policy are rendered from `/capabilities`, verbatim** —
 * the deployment's own statement about its boundaries, read from the enum under review rather than
 * written into this file. A screen paraphrasing "what we never put in a prompt" is the place that
 * sentence drifts from the code.
 *
 * **The air-gap refusal is shown early and enforced elsewhere.** A hosted backend is refused for an
 * air-gapped tenant by the domain, by the service with a 422, and by a CHECK constraint. All three
 * backends stay on offer and the band explains which is refused — an earlier build filtered the list
 * and left the page warning about a selection it no longer displayed.
 */

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { SERVING_BACKENDS } from '@/shared/api/generated/ai-serving';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card } from '@/shared/ui/Card';
import { CheckboxField, Field, SelectField, TextField } from '@/shared/ui/Field';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useConfigureRoute,
  useServingCapabilities,
  useServingRoute,
} from '../api/serving';
import type { ServingBackend, ServingRouteView } from '../api/serving';
import type { RouteState } from '../model/presentation';
import { backendKey, isRefusedPair, routeStateOf, stateKey } from '../model/presentation';

/** The reference's state card: a chip, then either the live values or what the state means.
 *
 * All three render always — that is the reference's layout and the point of its lede. The card
 * matching the tenant's actual state is the one carrying `Current:`, which is how the reference
 * distinguishes the live one without a separate "active" treatment.
 */
function StateCard({
  state,
  current,
  view,
}: {
  readonly state: RouteState;
  readonly current: boolean;
  readonly view: ServingRouteView | undefined;
}): ReactElement {
  const { t } = useTranslation();
  /* No top rule on the neutral card: in the reference the *absence* of a rule is the neutral
     state, and only the two states that mean something take a colour. */
  const rule =
    state === 'live'
      ? ' lq-ai__card--ok'
      : state === 'switched_off'
        ? ' lq-ai__card--warn'
        : '';
  const tone = state === 'live' ? 'ok' : state === 'switched_off' ? 'warn' : 'unknown';
  return (
    <div className={`lq-ai__card${rule}`}>
      <div className="lq-ai__chip-slot">
        <span
          className={`lq-ai__chip lq-ai__chip--${tone}`}
          title={t(`${stateKey(state)}Hint`)}
        >
          {/* Filled for a state that is a configuration somebody made, an outline for the blank —
              so "no route" reads as an absence in shape as well as in colour. */}
          <span
            className={`lq-ai__dot${state === 'unconfigured' ? '' : ' lq-ai__dot--filled'}`}
          />
          {t(stateKey(state))}
        </span>
      </div>
      <div className="lq-ai__card-body">
        {current && view?.configured === true ? (
          <>
            {t('serving.state.current')}{' '}
            {/* The reference prints `backend · host · model` in one mono run. The host rather than
                the full endpoint, matching what the audit chain stores — a path is not worth
                showing here and `origin_of` is the platform's own rule for the same field. */}
            <span className="lq-ai__mono">
              {[view.backend, hostOf(view.endpoint), view.model]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </>
        ) : (
          t(`${stateKey(state)}.body`)
        )}
      </div>
    </div>
  );
}

/** The endpoint's host, for the reference's one-line `backend · host · model` summary.
 *
 * Mirrors the platform's own `origin_of`: a stored endpoint is a base URL and the host is the part
 * that answers "where is this going". Falls back to the raw string rather than throwing, because a
 * value that reached here has already been through the domain's URL guard and a summary line is not
 * the place to discover otherwise.
 */
function hostOf(endpoint: string | undefined): string {
  if (endpoint === undefined || endpoint === '') return '';
  try {
    return new URL(endpoint).host;
  } catch {
    return endpoint;
  }
}

function Boundaries(): ReactElement | null {
  const { t } = useTranslation();
  const capabilities = useServingCapabilities();
  /* Silent when unreadable rather than reporting a failure. This card explains the platform's
   * boundaries and is not a statement about the tenant's route, so a 429 here must not make the
   * screen look broken — the route card below carries its own error state. */
  if (!capabilities.isSuccess) return null;
  const { rung, never_sent, purposes, max_prompt_chars, validator_policy } =
    capabilities.data;
  return (
    <Card title={t('serving.boundaries.title')}>
      <p>{t('serving.boundaries.rung', { rung })}</p>
      <p>{validator_policy}</p>
      <Field
        label={t('serving.boundaries.neverSent')}
        hint={t('serving.boundaries.neverSentHint')}
      >
        {() => (
          <ul>
            {never_sent.map((item) => (
              <li key={item}>
                <code>{item}</code>
              </li>
            ))}
          </ul>
        )}
      </Field>
      <Field
        label={t('serving.boundaries.purposes')}
        hint={t('serving.boundaries.purposesHint')}
      >
        {() => (
          <ul>
            {purposes.map((purpose) => (
              <li key={purpose.code}>
                <code>{purpose.code}</code> — {purpose.validated_by}
              </li>
            ))}
          </ul>
        )}
      </Field>
      <p>{t('serving.boundaries.promptCap', { chars: max_prompt_chars })}</p>
    </Card>
  );
}

export function ModelRoutingScreen(): ReactElement {
  const { t } = useTranslation();
  const route = useServingRoute();
  const configure = useConfigureRoute();

  const [backend, setBackend] = useState<ServingBackend>('llama_cpp');
  const [endpoint, setEndpoint] = useState('');
  const [model, setModel] = useState('');
  const [airGapped, setAirGapped] = useState(false);
  const [enabled, setEnabled] = useState(true);

  /* The form starts from the stored route, so an admin changing one field does not silently replace
     the other three with this component's defaults — a PUT replaces the whole route, so an unseeded
     form is a way to blank an endpoint by editing a model name. Keyed on the fetch rather than on
     mount because the route arrives after the first render. */
  useEffect(() => {
    if (!route.isSuccess || !route.data.configured) return;
    const stored = route.data;
    setBackend(stored.backend ?? 'llama_cpp');
    setEndpoint(stored.endpoint ?? '');
    setModel(stored.model ?? '');
    setAirGapped(stored.air_gapped ?? false);
    setEnabled(stored.enabled ?? true);
  }, [route.isSuccess, route.data]);

  const refused = isRefusedPair(backend, airGapped);
  const incomplete = endpoint.trim() === '' || model.trim() === '';
  const state = route.isSuccess ? routeStateOf(route.data) : undefined;
  const states: readonly RouteState[] = ['unconfigured', 'switched_off', 'live'];

  return (
    <div className="lq-ai">
      <div className="lq-ai__head">
        <div className="lq-ai__head-main">
          <h1 className="lq-ai__title">{t('serving.title')}</h1>
          <p className="lq-ai__lede">{t('serving.subtitle')}</p>
        </div>
      </div>

      {route.isPending && <SkeletonRows columns={3} rows={3} />}

      {route.isError && (
        <div className="lq-ai__stack">
          <Alert title={t('serving.readFailed')} tone="error">
            {route.error.message}
          </Alert>
        </div>
      )}

      {/* The reference's three-card legend. All three always, because the lede's claim is that the
          states must never collapse into two — and the one matching this tenant carries the values. */}
      {route.isSuccess && (
        <div className="lq-ai__triptych">
          {states.map((candidate) => (
            <StateCard
              current={candidate === state}
              key={candidate}
              state={candidate}
              view={route.data}
            />
          ))}
        </div>
      )}

      {/* The reference's refusal band, shown for the pair the platform will refuse. Not an `Alert`:
          the reference gives it no title row and no icon, just the status word set solid beside the
          explanation, inline in the reading order. */}
      {refused && (
        <div className="lq-ai__refusal">
          <strong className="lq-ai__refusal-tag">{t('serving.refusedPair.tag')}</strong>
          <span className="lq-ai__refusal-body">{t('serving.refusedPair.detail')}</span>
        </div>
      )}

      <div className="lq-ai__stack">
        {/* **No section heading, deliberately.** This used to be a card titled "Replace the
            route" with an intro paragraph above the fields. The reference has no such section —
            its only mention of storing a route is the empty state's own "Store a route below",
            which places the controls beneath the current-route panel and gives them no heading of
            their own. So the panel stays and the framing goes; nothing replaces it, because the
            reference contains no replacement.

            The intro's sentence is not lost from the product — the reference carries it as a
            *permission* state ("You can read this route, not change it") rather than as a section
            intro, which is a different screen state and is recorded rather than invented here. */}
        <div className="lq-ai__card">
          <SelectField
            hint={t('serving.field.backendHint')}
            label={t('serving.field.backend')}
            onChange={setBackend}
            options={SERVING_BACKENDS.map((option) => ({
              value: option,
              label: t(backendKey(option)),
            }))}
            value={backend}
          />

          <TextField
            hint={t('serving.field.endpointHint')}
            label={t('serving.field.endpoint')}
            maxLength={2000}
            onChange={setEndpoint}
            placeholder="http://llama.lumiq.svc.cluster.local:8080"
            required
            value={endpoint}
          />

          <TextField
            hint={t('serving.field.modelHint')}
            label={t('serving.field.model')}
            maxLength={200}
            onChange={setModel}
            placeholder="llama-3.1-8b-instruct"
            required
            value={model}
          />

          <CheckboxField
            checked={airGapped}
            hint={t('serving.field.airGappedHint')}
            label={t('serving.field.airGapped')}
            onChange={setAirGapped}
          />

          <CheckboxField
            checked={enabled}
            hint={t('serving.field.enabledHint')}
            label={t('serving.field.enabled')}
            onChange={setEnabled}
          />

          <Button
            disabled={configure.isPending || refused || incomplete}
            onClick={() => {
              configure.mutate({
                backend,
                endpoint: endpoint.trim(),
                model: model.trim(),
                air_gapped: airGapped,
                enabled,
              });
            }}
            tone="primary"
          >
            {t('serving.configure.action')}
          </Button>

          {/* The server's message verbatim. A 403 for a non-admin and a 422 for a route the domain
              refuses are both answers a person needs to read, and paraphrasing either is how a
              permissions answer comes to look like a bug. */}
          {configure.isError && (
            <Alert title={t('serving.configure.failed')} tone="error">
              {configure.error.message}
            </Alert>
          )}
          {configure.isSuccess && (
            <Alert title={t('serving.configure.stored')} tone="notice">
              {t('serving.configure.storedDetail')}
            </Alert>
          )}
        </div>

        <Boundaries />
      </div>

      {/* The reference's closing line, and the security model in one sentence: the credential is the
          deployment's, bounded by an allow-list, and this screen never asks for it. */}
      <p className="lq-ai__footnote">
        {t('serving.credentialNote', { variable: t('serving.credentialVariable') })}
      </p>
    </div>
  );
}
